import React, { memo } from 'react';
import { formats, validators } from 'investira.sdk';
import { Box, Card, Divider, Icon, Stack, Tooltip, Typography } from 'investira.react.components';

const wBenchmarkDivider = (
  <Box
    sx={{
      alignSelf: 'stretch',
      display: 'flex',
      width: {
        xs: '100%',
        sm: 'auto'
      }
    }}>
    <Divider
      sx={{
        display: {
          xs: 'block',
          sm: 'none'
        },
        width: '100%'
      }}
    />
    <Divider
      orientation="vertical"
      flexItem
      sx={{
        display: {
          xs: 'none',
          sm: 'block'
        }
      }}
    />
  </Box>
);

/**
 * Consolida os dados de desempenho relacionados ao benchmark em um único card.
 *
 * @param {Object} pProps - Propriedades do componente.
 * @param {Object} pProps.data - Dados da avaliação relacionados ao benchmark.
 * @returns {JSX.Element} Card consolidado do benchmark.
 */
const BenchmarkCard = memo(pProps => {
  const { data = {} } = pProps;
  const {
    benchmark_indicador_id: xBenchmark,
    perc_dias_acima_benchmark: xDiasAcima,
    perc_meses_acima_benchmark: xMesesAcima,
    amostragem: xAmostragem
  } = data;

  return (
    <Card
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%'
      }}>
      <Stack px={2} py={2} spacing={2} height="100%" justifyContent="space-between">
        <Stack>
          <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 500 }}>
            Benchmark
          </Typography>
          <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 500 }}>
            {xBenchmark || '---'}
          </Typography>
        </Stack>

        <Stack
          direction={{
            xs: 'column',
            sm: 'row'
          }}
          spacing={2}
          divider={wBenchmarkDivider}>
          <Stack flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 500 }}>
                {validators.isNull(xAmostragem) ? '---' : `${xAmostragem} meses`}
              </Typography>
              <Tooltip title="Quantidade de meses efetivamente usada no cálculo">
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <Icon iconName="information" size={16} color="primary" />
                </Box>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="textSecondary">
              Amostragem
            </Typography>
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 500 }}>
                {validators.isNull(xDiasAcima)
                  ? '---'
                  : `${formats.formatNumber(xDiasAcima, 2, true, false)}%`}
              </Typography>
              <Tooltip title="Percentual de dias da amostragem em que o fundo ficou acima do benchmark">
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <Icon iconName="information" size={16} color="primary" />
                </Box>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="textSecondary">
              Dias acima
            </Typography>
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 500 }}>
                {validators.isNull(xMesesAcima)
                  ? '---'
                  : `${formats.formatNumber(xMesesAcima, 2, true, false)}%`}
              </Typography>
              <Tooltip title="Percentual de meses da amostragem em que o fundo ficou acima do benchmark">
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <Icon iconName="information" size={16} color="primary" />
                </Box>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="textSecondary">
              Meses acima
            </Typography>
          </Stack>
        </Stack>

        {/* O retorno permanece separado no RetornoCard.
        {xHasRetorno && (
          <>
            <Divider />
            <Stack>
              <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 500 }}>
                Retorno em 12 meses
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" color={xRetornoColor} sx={{ fontWeight: 500 }}>
                  {!validators.isNull(xRetorno) ? formats.formatNumber(xRetorno, 3, true) : '---'}
                </Typography>
                <Tooltip title={wRetornoTooltip}>
                  <span>
                    <Icon iconName="information" size={16} color="primary" />
                  </span>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="textSecondary">
                {xRetornoDescription}
              </Typography>
            </Stack>
          </>
        )} */}
      </Stack>
    </Card>
  );
});

export default BenchmarkCard;
